// One place that turns PDF points into the number a person reads.
//
// The Measure tool owns the unit and the calibration factor, and those
// same two settings now label every resize: dragging a shape's corner
// and measuring across it must not disagree about how wide it is. The
// formatter travels by context rather than through the prop signature of
// every overlay view, because the consumers are leaf components several
// layers down (shape handles, image frames, fill frames, stamps) and
// none of them care about anything else the Measure tool knows.
import { createContext, useContext } from "react";
import { ptToUnit } from "./ui-constants.ts";

export type MeasureUnit = "pt" | "mm" | "cm" | "in";

/** Formats a length in PDF points for display, unit included. */
export type FormatLength = (pt: number) => string;

/** Points are shown to one decimal (a tenth of a point is already finer
 *  than any renderer resolves); the physical units get two, since a
 *  hundredth of a millimetre is the scale people actually check. */
export function makeFormatLength(unit: MeasureUnit, factor: number): FormatLength {
  return (pt) => {
    const value = ptToUnit(pt, unit) * factor;
    return `${value.toFixed(unit === "pt" ? 1 : 2)} ${unit}`;
  };
}

export const MeasureFormatContext = createContext<FormatLength>(makeFormatLength("mm", 1));

export function useFormatLength(): FormatLength {
  return useContext(MeasureFormatContext);
}
